import React, { useState, useEffect, useMemo } from 'react';
import { Button, Checkbox, Divider, Radio, Space, Table } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';

import { InputGroup } from '../InputGroup/InputGroup';
import {
  filteredSearchedData,
  getLowestKeys,
  AssociationsSelectorCss,
} from './AssociationsSelectorHelper';
import { alphabeticalColumnSort } from 'helpers/tableHelpers';
import { AssociationsSelectorActionGroup } from './AssociationsSelectorActionGroup';
import { TableLoading } from '../Table/TableLoading';
import { TableEmpty } from '../Table/TableEmpty';
import { CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons';

import { getTranslatedString } from 'helpers/i18n';
import { useWindowSize, sizes } from 'helpers/MediaHelper';
import { colors } from '../../styles/colors';

const StyledTableContainer = styled.div`
  height: ${(props) => props.height || '80vh'};
  overflow: scroll;
`;

const SearchAndActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
`;

const SearchAndCheckboxContainer = styled.div`
  width: 80%;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
`;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  width: max-content;
  align-items: baseline;
`;

const StyledSpace = styled(Space)`
  display: flex;
  flex-direction: row-reverse;
`;

const checkboxColumnLength = 1;

const OPTIONS_ALWAYS_AVAILABLE = [
  { value: 'Site', label: 'Sites' },
  { value: 'Property', label: 'Properties' },
  { value: 'UnitType', label: 'Unit Types' },
  { value: 'Unit', label: 'Units' },
];

const TREE_SEARCH_OPTIONS = [
  { value: 'Portfolio', label: 'Portfolios' },
  ...OPTIONS_ALWAYS_AVAILABLE,
];

const SMART_GROUPS_OPTIONS = [{ value: 'Group', label: 'Groups' }, ...OPTIONS_ALWAYS_AVAILABLE];

const StyledWrapper = styled.div`
  ${(props) => AssociationsSelectorCss(props.isMobile)}
`;

const AssociationsSelector = ({
  showExactMatchCheckbox = false,
  height,
  dataSource,
  dataSourceSmartGroups,
  loading,
  saveAction,
  cancelAction,
  onSelection,
  selectedRows,
  asyncSave,
  smartGroups,
}) => {
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [searchSelectOptions, setSearchSelectOptions] = useState(TREE_SEARCH_OPTIONS);
  const [dataToDisplay, setDataToDisplay] = useState([]);
  const [currentlySelectedRows, setCurrentlySelectedRows] = useState(selectedRows);
  const [selectVal, setSelectVal] = useState();
  const [inputVal, setInputVal] = useState();
  const [exactMatch, setExactMatch] = useState(false);
  const [mode, setMode] = useState('tree');

  const regionFilters = useMemo(() => {
    const dataToFilter = mode === 'tree' ? dataSource : dataSourceSmartGroups;
    return uniq(dataToFilter?.map((site) => site.regions)).map((r) => ({ text: r, value: r }));
  }, [mode, dataSource, dataSourceSmartGroups]);

  const cityFilters = useMemo(() => {
    const dataToFilter = mode === 'tree' ? dataSource : dataSourceSmartGroups;
    return uniq(dataToFilter?.map((site) => site.cities)).map((c) => ({ text: c, value: c }));
  }, [mode, dataSource, dataSourceSmartGroups]);

  useEffect(() => {
    const getClass = (obj) => {
      let classes = [];

      if (obj?.children) {
        obj.children.forEach((child_obj) => {
          let child_classes = getClass(child_obj);
          classes.push(...child_classes);
        });
      }

      if (obj?.class) {
        classes.push(obj.class);
      }

      return classes;
    };

    const classesInDataSource = getClass({
      children: mode === 'tree' ? dataSource : dataSourceSmartGroups,
    });

    const uniqueClassesFromDataSource = classesInDataSource.filter(
      (val, index, self) => val && self.indexOf(val) === index
    );

    const searchOptions = mode === 'tree' ? TREE_SEARCH_OPTIONS : SMART_GROUPS_OPTIONS;

    const validSearchOptions = searchOptions.filter((option) =>
      uniqueClassesFromDataSource.includes(option.value)
    );

    setSearchSelectOptions(validSearchOptions);
    setDataToDisplay(mode === 'tree' ? dataSource : dataSourceSmartGroups);
  }, [mode, dataSource, dataSourceSmartGroups]);

  const [width] = useWindowSize();
  const isMobile = width <= sizes.mobile;

  const columns = [
    {
      title: 'Item',
      dataIndex: 'item',
      filteredValue: filteredInfo.item || null,
      onFilter: (value, record) => record.item.indexOf(value) === 0,
      sorter: (a, b) => alphabeticalColumnSort(a.item, b.item),
      sortOrder: sortedInfo.field === 'item' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Regions',
      dataIndex: 'regions',
      filters: regionFilters,
      filteredValue: filteredInfo.regions || null,
      onFilter: (value, record) => record.regions.indexOf(value) === 0,
      sorter: (a, b) => alphabeticalColumnSort(a.regions, b.regions),
      sortOrder: sortedInfo.field === 'regions' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Cities',
      dataIndex: 'cities',
      filters: cityFilters,
      filteredValue: filteredInfo.cities || null,
      onFilter: (value, record) => record.cities.indexOf(value) === 0,
      sorter: (a, b) => alphabeticalColumnSort(a.cities, b.cities),
      sortOrder: sortedInfo.field === 'cities' && sortedInfo.order,
      ellipsis: true,
    },
  ];

  let args = {};

  if (loading) {
    // Overrides default table body renderer with custom loading component
    const loadingArgs = {
      components: {
        body: {
          wrapper: () => (
            <TableLoading
              data-testid="associations-selector-loading"
              colSpan={checkboxColumnLength + columns.length}
            />
          ),
        },
      },
      pagination: false,
    };

    args = { ...args, ...loadingArgs };
  }

  if (!dataToDisplay?.length && !loading) {
    // No results found or No data to Show message
    const message = getTranslatedString('tablesv4.noDataToShow');

    // Overrides default table body renderer with custom 'no data to show' component
    const noResultsArgs = {
      components: {
        body: {
          wrapper: () => (
            <TableEmpty
              data-testid="associations-selector-empty"
              colSpan={checkboxColumnLength + columns.length}
              message={message}
            />
          ),
        },
      },
    };

    args = { ...args, ...noResultsArgs };
  }

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const rowSelection = {
    selectedRowKeys: currentlySelectedRows,
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      let newRows = [...currentlySelectedRows];

      if (selected) {
        newRows = selectedRows.filter((row) => isEmpty(row.children)).map((row) => row.key);
      } else {
        let unselectedKeys = getLowestKeys(record);
        newRows = newRows.filter((row) => !unselectedKeys.includes(row));
      }

      setCurrentlySelectedRows([...newRows]);

      if (onSelection) {
        onSelection(newRows);
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      let newRows = [...currentlySelectedRows];
      let changedRows = changeRows.map((row) => row.key);

      if (selected) {
        newRows = selectedRows.filter((row) => isEmpty(row.children)).map((row) => row.key);
      } else {
        newRows = newRows.filter((row) => !changedRows.includes(row));
      }

      setCurrentlySelectedRows([...newRows]);

      if (onSelection) {
        onSelection(newRows);
      }
    },
  };

  const onSearch = (selectVal, inputVal, originalDataSource, exactMatch = false) => {
    setSelectVal(selectVal);
    setInputVal(inputVal);

    if (!inputVal) {
      setDataToDisplay([...(mode === 'tree' ? dataSource : dataSourceSmartGroups)]);
      return;
    }

    let newDataSource = [...originalDataSource];
    newDataSource = filteredSearchedData(selectVal, inputVal, newDataSource, exactMatch);

    setDataToDisplay(newDataSource);
  };

  const onCheckboxChange = (e) => {
    setExactMatch(e.target.checked);
    const dataSource = mode === 'tree' ? dataSource : dataSourceSmartGroups;
    onSearch(selectVal, inputVal, dataSource, e.target.checked);
  };

  const handleChangeMode = (e) => {
    setMode(e.target.value);
    setSelectVal(null);
    setCurrentlySelectedRows([]);
    setFilteredInfo({});
    setSortedInfo({});
    setInputVal('');
  };

  return (
    <StyledWrapper isMobile={isMobile}>
      <SearchAndActionContainer isMobile={isMobile}>
        <SearchAndCheckboxContainer isMobile={isMobile}>
          <InputGroup
            options={searchSelectOptions}
            defaultSelectValue={searchSelectOptions[0]?.value}
            onChange={(selectVal, inputVal) =>
              onSearch(
                selectVal,
                inputVal,
                mode === 'tree' ? dataSource : dataSourceSmartGroups,
                exactMatch
              )
            }
          />
          {showExactMatchCheckbox && (
            <StyledCheckbox onChange={onCheckboxChange}>
              {getTranslatedString('uiLibrary.associationsSelector.exactMatchesOnly')}
            </StyledCheckbox>
          )}
        </SearchAndCheckboxContainer>
        {!asyncSave && !smartGroups && (
          <AssociationsSelectorActionGroup
            currentlySelectedRows={currentlySelectedRows}
            saveAction={saveAction}
            cancelAction={cancelAction}
          />
        )}
        {smartGroups && (
          <Radio.Group value={mode} onChange={handleChangeMode}>
            <Radio.Button value="tree">
              {getTranslatedString('uiLibrary.associationsSelector.tree')}
            </Radio.Button>
            <Radio.Button value="smart_groups">
              {getTranslatedString('uiLibrary.associationsSelector.smartGroups')}
            </Radio.Button>
          </Radio.Group>
        )}
      </SearchAndActionContainer>
      <StyledTableContainer height={height}>
        <Table
          {...args}
          columns={columns}
          rowSelection={{ ...rowSelection, checkStrictly: false }}
          dataSource={dataToDisplay}
          onChange={handleChange}
          pagination={false}
          expandable={{
            expandIcon: ({ expanded, onExpand, record }) => {
              if (isEmpty(record.children)) {
                return <span style={{ marginRight: '22px' }}></span>;
              }

              if (expanded) {
                return (
                  <CaretDownOutlined
                    style={{ marginRight: '8px', color: `${colors['gray']['9']}` }}
                    onClick={(e) => onExpand(record, e)}
                  />
                );
              } else {
                return (
                  <CaretRightOutlined
                    style={{ marginRight: '8px', color: `${colors['gray']['9']}` }}
                    onClick={(e) => onExpand(record, e)}
                  />
                );
              }
            },
          }}
        />
      </StyledTableContainer>
    </StyledWrapper>
  );
};

AssociationsSelector.propTypes = {
  dataSource: PropTypes.array.isRequired,
  dataSourceSmartGroups: PropTypes.array,
  loading: PropTypes.bool,
  saveAction: PropTypes.func.isRequired,
  cancelAction: PropTypes.func.isRequired,

  // Callback for whenever rows are selected/unselected
  onSelection: PropTypes.func,

  // Set the currently selected rows
  selectedRows: PropTypes.arrayOf(PropTypes.string).isRequired,

  // Don't show the [Save | Cancel] button block. Providing
  // `true` here means the `saveAction` and `cancelAction` callbacks will
  // not be used.
  asyncSave: PropTypes.bool.isRequired,

  smartGroups: PropTypes.bool,
};

AssociationsSelector.defaultProps = {
  dataSource: [],
  dataSourceSmartGroups: [],
  loading: false,
  selectedRows: [],
  asyncSave: false,
  smartGroups: false,
};

export { AssociationsSelector };
